import { Seo } from "@/components/Seo";
import { Body } from "@/components/Editorial";
import { CriterioPage } from "@/components/Criterio";

const sections = [
  {
    id: "quello-che-si-scopre",
    title: "Quello che si scopre",
    content: (
      <>
        <Body>Chi arriva pensa di avere un problema. Sta scoprendo di non avere più opzioni.</Body>
        <Body>
          Trasformare una società di persone, separare un ramo, costituire una holding,
          riorganizzare le quote fra soci: sono operazioni che richiedono mesi. Perizie, delibere,
          atti notarili, termini a tutela dei creditori, adempimenti presso il Registro delle
          Imprese. In alcuni casi anche il consenso di soggetti che nel frattempo hanno interesse a
          non concederlo.
        </Body>
        <Body>
          Il bisogno arriva in settimane. La banca ha una scadenza per la pratica, il cliente ha un
          fornitore alternativo, il compratore ha altre due aziende sul tavolo, il socio che vuole
          uscire ha già smesso di lavorare come prima.
        </Body>
        <Body>
          Nel mezzo si perde l'operazione, oppure la si fa in condizioni peggiori di quelle che
          sarebbero state possibili con sei mesi di anticipo.
        </Body>
      </>
    ),
  },
  {
    id: "cosa-costa-il-ritardo",
    title: "Cosa costa il ritardo",
    content: (
      <>
        <Body>
          Un'operazione condotta in urgenza costa più di una condotta con margine: si comprime il
          lavoro, si pagano professionisti in emergenza, si accettano condizioni che con tempo
          davanti si sarebbero negoziate.
        </Body>
        <Body>
          E alcune cose non si recuperano. Una struttura si cambia in avanti. Un immobile rimasto
          per vent'anni dentro la società operativa ha accumulato un'esposizione che separarlo oggi
          non annulla. Una compagine costruita su accordi fra persone che nel frattempo hanno
          cambiato idea non si riscrive senza il consenso di chi trae vantaggio dal negarlo.
        </Body>
      </>
    ),
  },
  {
    id: "dove-sta-il-lavoro",
    title: "Dove sta il valore",
    content: (
      <>
        <Body>Il lavoro che conta si fa quando l'operazione non è ancora necessaria.</Body>
        <Body>
          Pianificazione societaria: la forma giuridica, la separazione fra ciò che opera e ciò che
          ha valore, la struttura di gruppo, i patti fra soci, le clausole statutarie che nessuno
          legge finché non servono e che a quel punto decidono tutto.
        </Body>
        <Body>Pianificazione finanziaria: la struttura che consente di finanziarsi quando servirà.</Body>
        <Body>
          Pianificazione patrimoniale: cosa accade al patrimonio se l'impresa va male, e cosa accade
          all'impresa se cambia chi la possiede.
        </Body>
        <Body>
          In quella fase si sceglie fra alternative. Dopo, si rimedia nel tempo disponibile.
        </Body>
      </>
    ),
  },
  {
    id: "con-chi-lavoro",
    title: "Con chi lavoro",
    content: (
      <>
        <Body>
          Imprese italiane, in tutta Italia. Società di persone e ditte individuali cresciute oltre
          la forma in cui sono nate, PMI e imprese familiari indicativamente fra i cinque e i
          cinquanta milioni di fatturato, gruppi con più società da riordinare. Industria, servizi,
          distribuzione.
        </Body>
        <Body>
          Lavoro anche con altri professionisti che hanno bisogno di un advisor specializzato per un
          cliente: in quel caso intervengo sulla singola operazione, e il rapporto con il cliente
          resta loro.
        </Body>
      </>
    ),
  },
  {
    id: "cinque-domande",
    title: "Cinque domande",
    content: (
      <>
        <Body>
          Se un cliente più strutturato chiedesse domani di contrattare con una società di capitali,
          in quanto tempo sarebbe possibile?
        </Body>
        <Body>
          Gli immobili strumentali sono nella stessa società che opera e che risponde delle
          obbligazioni d'impresa?
        </Body>
        <Body>
          Se un socio decidesse di uscire il mese prossimo, cosa prevedono statuto e patti?
        </Body>
        <Body>
          Se arrivasse un compratore interessato a un solo ramo dell'attività, quel ramo è
          separabile in tempi utili?
        </Body>
        <Body>Se domani chi guida l'impresa non ci fosse più, chi decide?</Body>
        <Body>
          Chi resta senza risposta su due di queste ha un problema che non è ancora arrivato. È il
          momento in cui costa meno affrontarlo.
        </Body>
      </>
    ),
  },
  {
    id: "come-lavoro",
    title: "Come lavoro",
    content: (
      <>
        <Body>
          In un'operazione societaria intervengono figure diverse, ciascuna competente sul proprio
          pezzo: il notaio sull'atto, l'avvocato sui contratti e sulle garanzie, il fiscalista sugli
          effetti tributari, l'esperto sulla stima, il consulente del lavoro sul personale. Il
          mandato di ciascuna si ferma al proprio perimetro, e il disegno complessivo resta senza un
          titolare.
        </Body>
        <Body>
          È il ruolo che ricopro. Definisco l'obiettivo con chi decide, scelgo la struttura fra le
          alternative possibili, coordino i professionisti coinvolti e verifico che ogni pezzo resti
          coerente con il resto fino all'atto.
        </Body>
        <Body>
          La perizia di stima la affido di regola a un terzo indipendente: chi struttura
          un'operazione e chi ne attesta i valori dovrebbero essere due persone diverse ogni volta
          che è possibile.
        </Body>
      </>
    ),
  },
  {
    id: "alcuni-esempi",
    title: "Alcuni esempi",
    content: (
      <>
        <Body>
          Riorganizzazione di un gruppo chimico industriale con presenza internazionale: quattro
          conferimenti in due anni, all'interno di un piano di risanamento attestato, con
          separazione dell'attività produttiva, delle unità territoriali e del patrimonio
          immobiliare.
        </Body>
        <Body>
          Separazione dell'attività operativa dal patrimonio immobiliare in un'impresa di servizi a
          conduzione familiare, con costituzione di una società dedicata.
        </Body>
        <Body>
          Mandato dei soci per la cessione di un'azienda di produzione, dopo un passaggio
          generazionale non riuscito.
        </Body>
        <Body>
          Assistenza a soci di minoranza e di maggioranza in operazioni di ingresso e di uscita
          dalla compagine.
        </Body>
        <Body>
          Dal 1996 lavoro su bilanci, finanza e assetti societari: in studio, poi come consulente
          per gruppi bancari e imprese industriali, poi con ruoli esecutivi e di governo in società
          operative. Oggi affianco imprese e soci sulle stesse materie, dall'interno delle
          operazioni.
        </Body>
      </>
    ),
  },
  {
    id: "se-vuoi-parlarne",
    title: "Se vuoi parlarne",
    content: (
      <Body>
        Se una delle cinque domande è rimasta senza risposta, o se c'è già una scadenza, scrivimi.
        La prima conversazione serve a capire quali strade sono ancora aperte e quanto tempo resta.
      </Body>
    ),
  },
];

export default function QuandoIlProblemaArriva() {
  return (
    <>
      <Seo
        title="Quando il problema arriva, le opzioni sono già finite · Alessandro Immobile"
        description="Cinque situazioni diverse, la stessa causa: una struttura societaria rimasta ferma mentre l'azienda si muoveva. Dove sta il valore, e quando conviene intervenire."
      />
      <CriterioPage
        testId="quando-page"
        titleTestId="quando-title"
        title="Quando il problema arriva, le opzioni sono già finite"
        lead="Alcune delle situazioni da cui nasce un incarico."
        intro={
          <>
            <Body>
              La banca ha sospeso l'istruttoria di un finanziamento: la delibera richiede bilanci
              depositati, e la società è una società di persone.
            </Body>
            <Body>
              Un cliente di dimensioni maggiori subordina la firma di un contratto quadro alla forma
              di società di capitali.
            </Body>
            <Body>
              Un figlio entra in azienda, l'altro ha preso un'altra strada, e la ripartizione delle
              quote non corrisponde più a chi l'impresa la manda avanti.
            </Body>
            <Body>Un socio vuole uscire.</Body>
            <Body>Un imprenditore valuta la cessione dell'azienda, o di un ramo.</Body>
            <Body>
              Sono cinque situazioni diverse e hanno la stessa forma. In tutte e cinque c'è
              un'impresa che si è mossa e una struttura societaria che è rimasta ferma. La forma
              giuridica è stata scelta quando l'azienda era un'altra cosa, spesso vent'anni prima, e
              da allora nessuno l'ha più guardata perché funzionava.
            </Body>
            <Body>
              Funzionava finché non è arrivata la banca, il cliente grande, il figlio, il socio che
              esce, il compratore.
            </Body>
          </>
        }
        sections={sections}
      />
    </>
  );
}
